import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import StepIndicator from './StepIndicator';

// Import all step components
import WelcomeScreen from './steps/WelcomeScreen';
import ExpertiseTypeStep from './steps/ExpertiseTypeStep';
import ExperienceCredentialsScreen from './steps/ExperienceCredentialsScreen';
import ServiceOfferingScreen from './steps/ServiceOfferingScreen';
import AvailabilityStep from './steps/AvailabilityStep';
import PricingScreen from './steps/PricingScreen';
import SessionFormatScreen from './steps/SessionFormatScreen';
import LanguagesScreen from './steps/LanguagesScreen';
import VerificationScreen from './steps/VerificationScreen';
import PayoutSetupScreen from './steps/PayoutSetupScreen';
import ProfilePreviewScreen from './steps/ProfilePreviewScreen';
import GuidelinesAgreementScreen from './steps/GuidelinesAgreementScreen';
import SuccessScreen from './steps/SuccessScreen';

import './ExpertOnboarding.css';

const ExpertOnboarding = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [onboardingData, setOnboardingData] = useState({
    // Welcome screen data
    agreedToStart: false,
    
    // Auth data
    userId: null,
    email: '',
    authProvider: '',
    
    // Profile data
    profilePhoto: null,
    name: '',
    headline: '',
    bio: '',
    
    // Expertise data
    expertiseType: '',
    secondaryExpertise: [],
    
    // Experience data
    experience: [],
    education: [],
    certificates: [],
    
    // Service data
    serviceTypes: [],
    availability: [],
    pricing: {
      hourlyRate: 0,
      promoDiscount: 0,
    },
    sessionFormats: [],
    languages: [],
    
    // Verification data
    verificationDocuments: [],
    stripeConnectId: '',
    
    // Agreement data
    agreedToGuidelines: false,
  });

  const navigate = useNavigate();
  const totalSteps = 14;

  // Load existing data if available
  useEffect(() => {
    const loadExistingData = async () => {
      const user = auth.currentUser;
      if (user) {
        const docRef = doc(db, 'experts', user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setOnboardingData(prev => ({
            ...prev,
            ...docSnap.data(),
            userId: user.uid
          }));
        }
      }
    };
    loadExistingData();
  }, []);

  // Save data to Firestore
  const saveProgress = async (data) => {
    if (!onboardingData.userId) return;
    try {
      await setDoc(doc(db, 'experts', onboardingData.userId), {
        ...onboardingData,
        ...data,
        lastUpdated: new Date(),
        currentStep
      }, { merge: true });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep === 1) {
      navigate(-1);
    } else {
      setCurrentStep(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleDataUpdate = async (data) => {
    const updatedData = {
      ...onboardingData,
      ...data
    };
    setOnboardingData(updatedData);
    await saveProgress(data);
  };

  const renderStep = () => {
    const commonProps = {
      onNext: handleNext,
      onBack: handleBack,
      onDataUpdate: handleDataUpdate,
      data: onboardingData,
    };

    switch (currentStep) {
      case 1:
        return <WelcomeScreen {...commonProps} />;
      case 2:
        return <ExpertiseTypeStep {...commonProps} />;
      case 3:
        return <ExperienceCredentialsScreen {...commonProps} />;
      case 4:
        return <ServiceOfferingScreen {...commonProps} />;
      case 5:
        return <AvailabilityStep {...commonProps} />;
      case 6:
        return <PricingScreen {...commonProps} />;
      case 7:
        return <SessionFormatScreen {...commonProps} />;
      case 8:
        return <LanguagesScreen {...commonProps} />;
      case 9:
        return <VerificationScreen {...commonProps} />;
      case 10:
        return <PayoutSetupScreen {...commonProps} />;
      case 11:
        return <ProfilePreviewScreen {...commonProps} />;
      case 12:
        return <GuidelinesAgreementScreen {...commonProps} />;
      case 13:
        return <SuccessScreen {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <div className="expert-onboarding">
      <div className="expert-onboarding__header">
        <button onClick={handleBack} className="back-button">
          Back
        </button>
        <div className="header-actions">
          <button 
            className="save-exit-button"
            onClick={() => navigate('/dashboard')}
          >
            Save & exit
          </button>
          <button 
            className="questions-button"
            onClick={() => window.open('/help', '_blank')}
          >
            Questions?
          </button>
        </div>
      </div>
      <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />
      <div className="expert-onboarding__content">
        {renderStep()}
      </div>
    </div>
  );
};

export default ExpertOnboarding; 